import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import CalorieProgress from '../components/CalorieProgress';
import FoodLogItem from '../components/FoodLogItem';
import Toast from '../components/Toast';
import { copyDay, copyMeal } from '../utils/nutritionApi';

const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'];
const MEAL_LABELS = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack'
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const toDateStr = (d) => d.toISOString().split('T')[0];

  if (dateStr === toDateStr(today)) return 'Today';
  if (dateStr === toDateStr(yesterday)) return 'Yesterday';
  if (dateStr === toDateStr(tomorrow)) return 'Tomorrow';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function FoodLog() {
  const { date: dateParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const todayStr = new Date().toISOString().split('T')[0];
  const selectedDate = dateParam || todayStr;

  const [logs, setLogs] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [loading, setLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [toast, setToast] = useState(null);

  // Copy Day state
  const [showCopyDay, setShowCopyDay] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(todayStr);
  const [copyMealTypes, setCopyMealTypes] = useState([]);
  const [copyLoading, setCopyLoading] = useState(false);
  const [targetDayCount, setTargetDayCount] = useState(0);

  // Copy Meal state
  const [copyMealGroup, setCopyMealGroup] = useState(null);
  const [copyMealTargetDate, setCopyMealTargetDate] = useState(todayStr);
  const [copyMealTargetType, setCopyMealTargetType] = useState('');
  const [copyMealLoading, setCopyMealLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/logs?date=${selectedDate}`);
      setLogs(res.data.logs);
      setTotals(res.data.totals);
    } catch (err) {
      console.error('Failed to fetch logs:', err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Check target date entry count when copy day panel opens or target changes
  useEffect(() => {
    if (!showCopyDay) return;
    if (copyTargetDate === selectedDate) {
      setTargetDayCount(-1); // same day indicator
      return;
    }
    api.get(`/api/logs?date=${copyTargetDate}`)
      .then(res => setTargetDayCount(res.data.logs.length))
      .catch(() => setTargetDayCount(0));
  }, [showCopyDay, copyTargetDate, selectedDate]);

  const navigateDate = (offset) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    const newDate = d.toISOString().split('T')[0];
    navigate(newDate === todayStr ? '/log' : `/log/${newDate}`);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setShowDatePicker(false);
    if (newDate) {
      navigate(newDate === todayStr ? '/log' : `/log/${newDate}`);
    }
  };

  const recalcTotals = (logList) => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    for (const log of logList) {
      const qty = parseFloat(log.quantity) || 1;
      t.calories += (log.calories || 0) * qty;
      t.protein += parseFloat(log.protein || 0) * qty;
      t.carbs += parseFloat(log.carbs || 0) * qty;
      t.fat += parseFloat(log.fat || 0) * qty;
      t.fiber += parseFloat(log.fiber || 0) * qty;
    }
    t.protein = Math.round(t.protein * 10) / 10;
    t.carbs = Math.round(t.carbs * 10) / 10;
    t.fat = Math.round(t.fat * 10) / 10;
    t.fiber = Math.round(t.fiber * 10) / 10;
    setTotals(t);
  };

  const handleDelete = (id) => {
    setLogs(prev => {
      const updated = prev.filter(l => l.id !== id);
      recalcTotals(updated);
      return updated;
    });
  };

  const handleUpdate = (updatedLog) => {
    setLogs(prev => {
      const updated = prev.map(l => l.id === updatedLog.id ? updatedLog : l);
      recalcTotals(updated);
      return updated;
    });
  };

  // Group logs by meal_type
  const grouped = {};
  for (const type of MEAL_ORDER) {
    grouped[type] = [];
  }
  for (const log of logs) {
    const type = log.meal_type || 'snack';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(log);
  }

  const calorieGoal = user?.daily_calorie_goal || 2000;
  const isToday = selectedDate === todayStr;
  const isFuture = selectedDate > todayStr;

  // Copy Day helpers
  const toggleCopyMealType = (type) => {
    setCopyMealTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const getCopyPreview = () => {
    let items = logs;
    if (copyMealTypes.length > 0) {
      items = logs.filter(l => copyMealTypes.includes(l.meal_type || 'snack'));
    }
    const totalCal = items.reduce((sum, l) => sum + Math.round((l.calories || 0) * (parseFloat(l.quantity) || 1)), 0);
    return { count: items.length, calories: totalCal };
  };

  const handleCopyDay = async () => {
    if (copyTargetDate === selectedDate) return;
    setCopyLoading(true);
    try {
      const mealTypes = copyMealTypes.length > 0 ? copyMealTypes : undefined;
      const newEntries = await copyDay(selectedDate, copyTargetDate, mealTypes);
      setToast({ message: `Copied ${newEntries.length} item${newEntries.length !== 1 ? 's' : ''} to ${formatDateLabel(copyTargetDate)}`, type: 'success' });
      setShowCopyDay(false);
    } catch (err) {
      console.error('Copy day failed:', err.message);
      setToast({ message: 'Failed to copy meals', type: 'error' });
    } finally {
      setCopyLoading(false);
    }
  };

  const openCopyDay = () => {
    setCopyTargetDate(todayStr);
    setCopyMealTypes([]);
    setShowCopyDay(true);
  };

  // Copy Meal helpers
  const openCopyMeal = (mealType) => {
    setCopyMealGroup(mealType);
    setCopyMealTargetDate(todayStr);
    setCopyMealTargetType('');
  };

  const handleCopyMeal = async () => {
    const items = grouped[copyMealGroup];
    if (!items || items.length === 0) return;
    setCopyMealLoading(true);
    try {
      const logIds = items.map(l => l.id);
      const targetType = copyMealTargetType || undefined;
      const newEntries = await copyMeal(logIds, copyMealTargetDate, targetType);
      setToast({ message: `Copied ${newEntries.length} item${newEntries.length !== 1 ? 's' : ''} to ${formatDateLabel(copyMealTargetDate)}`, type: 'success' });
      setCopyMealGroup(null);
    } catch (err) {
      console.error('Copy meal failed:', err.message);
      setToast({ message: 'Failed to copy meal', type: 'error' });
    } finally {
      setCopyMealLoading(false);
    }
  };

  const copyPreview = showCopyDay ? getCopyPreview() : null;

  return (
    <div className="page foodlog-page">
      {/* Date navigator */}
      <div className="foodlog-date-nav">
        <button className="foodlog-nav-btn" onClick={() => navigateDate(-1)}>
          &#8249;
        </button>
        <button
          className="foodlog-date-label"
          onClick={() => setShowDatePicker(!showDatePicker)}
        >
          <span className="foodlog-date-text">{formatDateLabel(selectedDate)}</span>
          <span className="foodlog-date-sub">
            {isToday ? '' : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </button>
        <button
          className="foodlog-nav-btn"
          onClick={() => navigateDate(1)}
          disabled={isFuture}
        >
          &#8250;
        </button>
      </div>

      {/* Date picker (shown on tap) */}
      {showDatePicker && (
        <div className="foodlog-picker-row">
          <input
            type="date"
            className="date-input"
            value={selectedDate}
            max={todayStr}
            onChange={handleDateChange}
            autoFocus
            onBlur={() => setTimeout(() => setShowDatePicker(false), 200)}
          />
        </div>
      )}

      {/* Calorie summary */}
      <div className="card">
        <CalorieProgress current={Math.round(totals.calories)} goal={calorieGoal} />
      </div>

      {/* Macros mini row */}
      <div className="macros-row">
        <div className="macro-item">
          <span className="macro-value">{totals.protein}g</span>
          <span className="macro-label">Protein</span>
        </div>
        <div className="macro-item">
          <span className="macro-value">{totals.carbs}g</span>
          <span className="macro-label">Carbs</span>
        </div>
        <div className="macro-item">
          <span className="macro-value">{totals.fat}g</span>
          <span className="macro-label">Fat</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="foodlog-actions">
        <Link to="/search" className="btn btn-primary add-food-btn" style={{ flex: 1 }}>
          + Add Food
        </Link>
        {logs.length > 0 && (
          <button className="btn btn-outline copy-day-btn" onClick={openCopyDay}>
            Copy Day
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && logs.length === 0 && (
        <div className="empty-log">
          <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
            No foods logged for {isToday ? 'today' : 'this day'}.
          </p>
          <p className="text-muted">
            Start by <Link to="/search" className="auth-link">searching for a food</Link>!
          </p>
        </div>
      )}

      {/* Grouped meal sections */}
      {!loading && logs.length > 0 && (
        <div className="foodlog-meals">
          {MEAL_ORDER.map(type => {
            const items = grouped[type];
            if (!items || items.length === 0) return null;

            const mealCals = items.reduce((sum, l) => sum + Math.round((l.calories || 0) * (l.quantity || 1)), 0);

            return (
              <div key={type} className="meal-group">
                <div className="meal-group-header">
                  <span className="meal-group-name">{MEAL_LABELS[type]}</span>
                  <div className="meal-group-actions">
                    <span className="meal-group-cal">{mealCals} cal</span>
                    <button
                      className="meal-copy-btn"
                      onClick={() => openCopyMeal(type)}
                      title={`Copy ${MEAL_LABELS[type]}`}
                    >
                      &#x2398;
                    </button>
                  </div>
                </div>
                <div className="food-log-list">
                  {items.map(log => (
                    <FoodLogItem
                      key={log.id}
                      log={log}
                      onDelete={handleDelete}
                      onUpdate={handleUpdate}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Copy Day Modal ===== */}
      {showCopyDay && (
        <div className="copy-overlay" onClick={() => setShowCopyDay(false)}>
          <div className="copy-panel" onClick={e => e.stopPropagation()}>
            <div className="copy-panel-header">
              <h3>Copy Day</h3>
              <button className="copy-panel-close" onClick={() => setShowCopyDay(false)}>&times;</button>
            </div>

            <p className="copy-panel-source">
              From: <strong>{formatDateLabel(selectedDate)}</strong>
            </p>

            {/* Filter meal types */}
            <div className="copy-panel-field">
              <label>Meals to copy</label>
              <div className="copy-meal-filter">
                {MEAL_ORDER.map(type => {
                  const count = grouped[type]?.length || 0;
                  if (count === 0) return null;
                  return (
                    <button
                      key={type}
                      className={`copy-meal-chip ${copyMealTypes.length === 0 || copyMealTypes.includes(type) ? 'active' : ''}`}
                      onClick={() => toggleCopyMealType(type)}
                    >
                      {MEAL_LABELS[type]} ({count})
                    </button>
                  );
                })}
              </div>
              <span className="copy-hint">
                {copyMealTypes.length === 0 ? 'All meals selected' : `${copyMealTypes.length} meal type${copyMealTypes.length !== 1 ? 's' : ''} selected`}
              </span>
            </div>

            {/* Target date */}
            <div className="copy-panel-field">
              <label>Copy to date</label>
              <input
                type="date"
                className="date-input"
                value={copyTargetDate}
                onChange={(e) => setCopyTargetDate(e.target.value)}
              />
            </div>

            {/* Warning */}
            {targetDayCount > 0 && (
              <div className="copy-warning">
                This day already has {targetDayCount} entr{targetDayCount === 1 ? 'y' : 'ies'}. New foods will be added alongside existing ones.
              </div>
            )}
            {targetDayCount === -1 && (
              <div className="copy-warning copy-warning-error">
                Cannot copy to the same date.
              </div>
            )}

            {/* Preview */}
            {copyPreview && copyPreview.count > 0 && (
              <div className="copy-preview">
                <span>{copyPreview.count} item{copyPreview.count !== 1 ? 's' : ''}</span>
                <span className="copy-preview-cal">{copyPreview.calories} cal</span>
              </div>
            )}

            <button
              className="btn btn-primary btn-full"
              onClick={handleCopyDay}
              disabled={copyLoading || copyTargetDate === selectedDate || (copyPreview && copyPreview.count === 0)}
            >
              {copyLoading ? 'Copying...' : 'Copy Meals'}
            </button>
          </div>
        </div>
      )}

      {/* ===== Copy Meal Modal ===== */}
      {copyMealGroup && (
        <div className="copy-overlay" onClick={() => setCopyMealGroup(null)}>
          <div className="copy-panel copy-panel-compact" onClick={e => e.stopPropagation()}>
            <div className="copy-panel-header">
              <h3>Copy {MEAL_LABELS[copyMealGroup]}</h3>
              <button className="copy-panel-close" onClick={() => setCopyMealGroup(null)}>&times;</button>
            </div>

            <p className="copy-panel-source">
              {grouped[copyMealGroup]?.length || 0} item{(grouped[copyMealGroup]?.length || 0) !== 1 ? 's' : ''} &middot;{' '}
              {grouped[copyMealGroup]?.reduce((s, l) => s + Math.round((l.calories || 0) * (l.quantity || 1)), 0)} cal
            </p>

            <div className="copy-panel-field">
              <label>Copy to date</label>
              <input
                type="date"
                className="date-input"
                value={copyMealTargetDate}
                onChange={(e) => setCopyMealTargetDate(e.target.value)}
              />
            </div>

            <div className="copy-panel-field">
              <label>Change meal type (optional)</label>
              <div className="meal-selector">
                <button
                  className={`meal-btn ${copyMealTargetType === '' ? 'active' : ''}`}
                  onClick={() => setCopyMealTargetType('')}
                >
                  Keep
                </button>
                {MEAL_ORDER.map(type => (
                  <button
                    key={type}
                    className={`meal-btn ${copyMealTargetType === type ? 'active' : ''}`}
                    onClick={() => setCopyMealTargetType(type)}
                  >
                    {MEAL_LABELS[type]}
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn btn-primary btn-full"
              onClick={handleCopyMeal}
              disabled={copyMealLoading}
            >
              {copyMealLoading ? 'Copying...' : 'Copy Meal'}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default FoodLog;
